// Create or edit a class: name (required), subject, grade range and school year.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { T } from '../gamification/shared';
import type { ClassInput, SchoolClass } from '../../services/classService';
import { errorMessage, errorStyle, inputStyle, labelStyle, primaryButtonStyle, secondaryButtonStyle } from './classesStyles';

interface Props {
  /** The class being edited; omitted when creating. */
  initial?: SchoolClass | null;
  onSubmit: (input: ClassInput) => Promise<void>;
  onClose: () => void;
}

const FIELDS: Array<{ key: 'name' | 'subject' | 'grade_range' | 'school_year'; maxLength: number; placeholderKey?: string }> = [
  { key: 'name', maxLength: 255 },
  { key: 'subject', maxLength: 255 },
  { key: 'grade_range', maxLength: 50, placeholderKey: 'classes.gradeRangePlaceholder' },
  { key: 'school_year', maxLength: 20, placeholderKey: 'classes.schoolYearPlaceholder' },
];

const ClassFormModal: React.FC<Props> = ({ initial, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({
    name: initial?.name ?? '',
    subject: initial?.subject ?? '',
    grade_range: initial?.grade_range ?? '',
    school_year: initial?.school_year ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setError(t('classes.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: values.name.trim(),
        // An emptied field is sent as null so editing can clear it.
        subject: values.subject.trim() || null,
        grade_range: values.grade_range.trim() || null,
        school_year: values.school_year.trim() || null,
      });
    } catch (err) {
      setError(errorMessage(err, t('classes.couldNotSaveClass')));
      setSaving(false);
    }
  };

  const title = initial ? t('classes.editClass') : t('classes.createClass');

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={submit}
        style={{
          background: T.card,
          borderRadius: T.radius,
          padding: '1.5rem',
          width: '100%',
          maxWidth: 460,
          fontFamily: T.font,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.2rem', color: T.text }}>{title}</h2>
        {error && <div style={errorStyle} role="alert">{error}</div>}
        {FIELDS.map(field => (
          <div key={field.key} style={{ marginBottom: '0.9rem' }}>
            <label htmlFor={`class-${field.key}`} style={labelStyle}>
              {t(`classes.fields.${field.key}`)}
              {field.key === 'name' && ' *'}
            </label>
            <input
              id={`class-${field.key}`}
              style={{ ...inputStyle, width: '100%' }}
              value={values[field.key]}
              maxLength={field.maxLength}
              placeholder={field.placeholderKey ? t(field.placeholderKey) : undefined}
              autoFocus={field.key === 'name'}
              onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
            />
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <button type="button" style={secondaryButtonStyle} onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="submit" style={primaryButtonStyle} disabled={saving}>
            {saving ? t('classes.saving') : initial ? t('classes.save') : t('classes.create')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default ClassFormModal;
